
import * as Cesium from "cesium";
/**
 * Generate orbital track points from orbital elements
 * @param orbitalElements - Object containing orbital parameters
 * @param orbitalElements.altitude - Altitude in kilometers (will be converted to orbital radius)
 * @param orbitalElements.eccentricity - Orbital eccentricity (0 for circular, <1 for elliptical)
 * @param orbitalElements.inclination - Inclination in degrees
 * @param orbitalElements.RAAN - Right Ascension of Ascending Node in degrees
 * @param orbitalElements.argumentOfPeriapsis - Argument of Periapsis in degrees (optional, default 0)
 * @param numPoints - Number of points to generate for the orbit (default 360)
 * @returns Array of Cesium.Cartesian3 points representing the orbital track
 */
export const generateOrbitalTrackFromElements = (
    orbitalElements: {
        altitude: number,
        eccentricity: number,
        inclination: number,
        RAAN: number,
        argumentOfPeriapsis?: number
    },
    numPoints: number = 360
): Cesium.Cartesian3[] => {
    const EARTH_RADIUS_KM = 6371.0 // Earth's mean radius in kilometers
    
    const { altitude, eccentricity, inclination, RAAN, argumentOfPeriapsis = 0 } = orbitalElements
    
    // Convert altitude to orbital radius (in meters for Cesium)
    const orbitalRadius = (altitude + EARTH_RADIUS_KM) * 1000 // Convert km to meters
    
    // Convert angles from degrees to radians
    const inclinationRad = Cesium.Math.toRadians(inclination)
    const RAANRad = Cesium.Math.toRadians(RAAN)
    const argPeriapsisRad = Cesium.Math.toRadians(argumentOfPeriapsis)
    
    const orbitalTrackPoints: Cesium.Cartesian3[] = []
    
    for (let i = 0; i < numPoints; i++) {
        // True anomaly angle around the orbit
        const trueAnomaly = (i / numPoints) * 2 * Math.PI
        
        // Calculate radius at this point in the orbit (accounts for eccentricity)
        // For elliptical orbits: r = a(1-e²)/(1+e*cos(TA))
        const radiusAtPoint = eccentricity > 0
            ? orbitalRadius * (1 - eccentricity * eccentricity) / (1 + eccentricity * Math.cos(trueAnomaly))
            : orbitalRadius
        
        // Position in orbital plane (perifocal coordinate system)
        const xPerifocal = radiusAtPoint * Math.cos(trueAnomaly)
        const yPerifocal = radiusAtPoint * Math.sin(trueAnomaly)
        
        // Rotate by argument of periapsis (within the orbital plane)
        const xOrbit = xPerifocal * Math.cos(argPeriapsisRad) - yPerifocal * Math.sin(argPeriapsisRad)
        const yOrbit = xPerifocal * Math.sin(argPeriapsisRad) + yPerifocal * Math.cos(argPeriapsisRad)
        
        // Rotate by inclination (around X axis) - tilts the orbital plane
        const xRotOrbit = xOrbit
        const yRotOrbit = yOrbit * Math.cos(inclinationRad)
        const zRotOrbit = yOrbit * Math.sin(inclinationRad)
        
        // Rotate by RAAN (around Z axis) - rotates the ascending node
        const xFinal = xRotOrbit * Math.cos(RAANRad) - yRotOrbit * Math.sin(RAANRad)
        const yFinal = xRotOrbit * Math.sin(RAANRad) + yRotOrbit * Math.cos(RAANRad)
        const zFinal = zRotOrbit
        
        orbitalTrackPoints.push(new Cesium.Cartesian3(xFinal, yFinal, zFinal))
    }
    
    // Close the orbit loop by adding the first point at the end
    orbitalTrackPoints.push(orbitalTrackPoints[0])
    
    return orbitalTrackPoints
}